<template>
  <div class="bg-brand-purple-dark text-gray-300">
    <!-- Header -->
    <header class="container mx-auto px-6 py-4 flex justify-between items-center">
      <div class="flex items-center">
        <img src="@/assets/logo.png" alt="Castive Logo" class="h-8 w-auto">
        <span class="text-white text-2xl font-bold ml-2">Castive</span>
      </div>
      <nav>
        <router-link to="/login" class="bg-white/10 text-white font-semibold py-2 px-5 rounded-lg hover:bg-white/20 transition-colors">
          Client Login
        </router-link>
      </nav>
    </header>

    <!-- Hero Section -->
    <main class="container mx-auto px-6">
      <section class="text-center py-20 lg:py-32">
        <h1 class="text-4xl md:text-6xl font-extrabold text-white leading-tight">
          The Effortless Podcast Platform
          <br>
          Powered by
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-brand-yellow via-brand-orange to-brand-pink">
            Cutting-Edge AI
          </span>
        </h1>
        <p class="mt-6 max-w-2xl mx-auto text-lg text-gray-400">
          Castive is the all-in-one, white-label SaaS solution that automates your workflow, from content generation to distribution, so you can focus on creating.
        </p>
        <div class="mt-10">
          <a href="#contact" class="bg-gradient-to-r from-brand-orange to-brand-pink text-white font-bold py-4 px-10 rounded-full text-lg hover:opacity-90 transition-opacity">
            Request a Demo
          </a>
        </div>
      </section>

      <!-- NEW: SMART VIDEO FEATURE SECTION -->
      <section class="max-w-5xl mx-auto mb-20 px-4">
        <div class="relative group overflow-hidden rounded-3xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] bg-black/40 aspect-video">
          
          <!-- Audio Hint Overlay -->
          <div class="absolute top-6 right-6 z-10 bg-black/60 backdrop-blur-md text-white text-[10px] tracking-widest font-bold px-4 py-2 rounded-full pointer-events-none flex items-center gap-2 border border-white/10 transition-opacity duration-300">
            <i class="fas" :class="isMuted ? 'fa-volume-up' : 'fa-volume-mute'"></i>
            {{ isMuted ? 'TAP FOR SOUND' : 'MUTE' }}
          </div>

          <video
            ref="featureVideo"
            src="@/assets/promo-video.mp4"
            autoplay
            loop
            muted
            playsinline
            class="w-full h-full object-cover cursor-pointer transition-transform duration-700 group-hover:scale-105"
            @mouseover="playWithSound"
            @mouseleave="pauseAndMute"
            @click="toggleAudioMobile"
          ></video>
        </div>
      </section>

      <!-- Features Section -->
      <section class="py-20">
        <div class="grid md:grid-cols-3 gap-10 text-center">
          <div class="bg-white/5 p-8 rounded-2xl">
            <h3 class="text-2xl font-bold text-white">White-Label & Multi-Tenant</h3>
            <p class="mt-4 text-gray-400">Manage multiple podcast clients under one roof, each with their own secure dashboard and custom branding. The perfect solution for agencies and networks.</p>
          </div>
          <div class="bg-white/5 p-8 rounded-2xl">
            <h3 class="text-2xl font-bold text-white">Automated AI Content</h3>
            <p class="mt-4 text-gray-400">Upload your audio, and our AI generates professional titles, detailed show notes, and full, accurate transcripts in minutes. Reclaim your time.</p>
          </div>
          <div class="bg-white/5 p-8 rounded-2xl">
            <h3 class="text-2xl font-bold text-white">Modern & Compliant RSS</h3>
            <p class="mt-4 text-gray-400">We generate a Podcasting 2.0-compliant RSS feed, ensuring maximum compatibility and discoverability on platforms like Spotify and Apple Podcasts.</p>
          </div>
        </div>
      </section>

      <!-- Slideshow Section -->
      <section class="py-20">
        <h2 class="text-4xl font-bold text-center text-white">A Glimpse Inside Castive</h2>
        <p class="text-lg text-gray-400 text-center mt-4 mb-12">An intuitive interface designed for power and simplicity.</p>
        
        <div class="relative w-full max-w-4xl mx-auto group">
          <div class="relative h-[36rem] w-full bg-black/20 rounded-xl shadow-2xl overflow-hidden">
            <transition-group name="fade" tag="div">
              <div v-for="(image, index) in images" :key="image" v-show="currentIndex === index" class="absolute inset-0">
                <img :src="image" alt="Castive app screenshot" class="w-full h-full object-contain">
              </div>
            </transition-group>
          </div>

          <button @click="prevSlide" class="absolute top-1/2 left-4 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button @click="nextSlide" class="absolute top-1/2 right-4 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
          </button>

          <div class="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
            <button v-for="(image, index) in images" :key="`dot-${index}`" @click="goToSlide(index)" 
              class="w-3 h-3 rounded-full transition-colors"
              :class="currentIndex === index ? 'bg-white' : 'bg-white/50 hover:bg-white/75'">
            </button>
          </div>
        </div>
      </section>
      
      <!-- CTA Section -->
      <section id="contact" class="text-center py-20 bg-white/5 rounded-2xl my-20">
        <h2 class="text-4xl font-bold text-white">Ready to Cast Smarter?</h2>
        <p class="mt-4 max-w-xl mx-auto text-lg text-gray-400">
          Let's talk about how Castive can streamline your podcast production. Our registration and subscription portal is coming soon!
        </p>
        <div class="mt-8">
          <a href="mailto:sales@castive.app" class="bg-gradient-to-r from-brand-orange to-brand-pink text-white font-bold py-4 px-10 rounded-full text-lg hover:opacity-90 transition-opacity">
            Contact Sales
          </a>
        </div>
      </section>
    </main>

    <!-- Footer -->
    <footer class="text-center py-8 border-t border-white/10">
      <p class="text-gray-500">&copy; {{ new Date().getFullYear() }} Castive. All Rights Reserved.</p>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'LandingPage',
  data() {
    return {
      isMuted: true,
      currentIndex: 0,
      slideInterval: null,
      images: [
        'https://firebasestorage.googleapis.com/v0/b/castive-prod.firebasestorage.app/o/landing-slideshow%2FChatGPT%20Image%20Sep%2028%2C%202025%2C%2003_03_22%20PM.png?alt=media&token=85299b15-58f9-4543-af64-e1c9d40a20b3',
        'https://firebasestorage.googleapis.com/v0/b/castive-prod.firebasestorage.app/o/landing-slideshow%2FChatGPT%20Image%20Sep%2028%2C%202025%2C%2003_06_36%20PM.png?alt=media&token=dcc2bed2-561f-4d9a-92e8-12bf289ab730',
        'https://firebasestorage.googleapis.com/v0/b/castive-prod.firebasestorage.app/o/landing-slideshow%2FChatGPT%20Image%20Sep%2028%2C%202025%2C%2003_11_43%20PM.png?alt=media&token=677638e5-6462-4201-b643-bef69bc8337c',
        'https://firebasestorage.googleapis.com/v0/b/castive-prod.firebasestorage.app/o/landing-slideshow%2FChatGPT%20Image%20Sep%2028%2C%202025%2C%2003_13_33%20PM.png?alt=media&token=f41c9a58-dfbc-4463-846e-087d4a44dd07',
        'https://firebasestorage.googleapis.com/v0/b/castive-prod.firebasestorage.app/o/landing-slideshow%2FChatGPT%20Image%20Sep%2028%2C%202025%2C%2003_15_28%20PM.png?alt=media&token=808ed4df-0b85-412c-8e03-c13fc31b4c77',
        'https://firebasestorage.googleapis.com/v0/b/castive-prod.firebasestorage.app/o/landing-slideshow%2FChatGPT%20Image%20Sep%2028%2C%202025%2C%2003_17_28%20PM.png?alt=media&token=ecc52dc5-3185-4c48-b730-96d02235b947',
        'https://firebasestorage.googleapis.com/v0/b/castive-prod.firebasestorage.app/o/landing-slideshow%2FChatGPT%20Image%20Sep%2028%2C%202025%2C%2003_20_34%20PM.png?alt=media&token=07035d4d-0cf4-4ca3-ae19-f34924ebc715',
        'https://firebasestorage.googleapis.com/v0/b/castive-prod.firebasestorage.app/o/landing-slideshow%2FChatGPT%20Image%20Sep%2028%2C%202025%2C%2003_27_08%20PM.png?alt=media&token=b9ca63bd-17a7-44e7-af4f-3b9406e48caa'
      ],
    };
  },
  methods: {
    playWithSound() {
      const video = this.$refs.featureVideo;
      if (video) {
        video.play();
        video.muted = false;
        this.isMuted = false;
      }
    },
    pauseAndMute() {
      const video = this.$refs.featureVideo;
      if (video) {
        video.pause();
        video.muted = true;
        this.isMuted = true;
      }
    },
    toggleAudioMobile() {
      const video = this.$refs.featureVideo;
      if (video) {
        if (video.paused || video.muted) {
          video.play();
          video.muted = false;
          this.isMuted = false;
        } else {
          video.pause();
          video.muted = true;
          this.isMuted = true;
        }
      }
    },
    nextSlide() {
      this.currentIndex = (this.currentIndex + 1) % this.images.length;
    },
    prevSlide() {
      this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    },
    goToSlide(index) {
      this.currentIndex = index;
    },
  },
  mounted() {
    this.slideInterval = setInterval(this.nextSlide, 5000);
  },
  beforeUnmount() {
    clearInterval(this.slideInterval);
  },
  beforeCreate() {
    document.documentElement.style.setProperty('--color-brand-yellow', '#FED803');
    document.documentElement.style.setProperty('--color-brand-orange', '#E7643B');
    document.documentElement.style.setProperty('--color-brand-pink', '#D91C5E');
    document.documentElement.style.setProperty('--color-brand-purple-dark', '#190E4A');
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>